import { Link } from "react-router-dom";
import VerdictBadge from "@/components/base/VerdictBadge";
import CredibilityMeter from "@/components/base/CredibilityMeter";
import { formatHeat } from "@/utils/verdict";
import type { GossipItem } from "@/types";

export default function GossipCard({
  item,
  variant = "default",
}: {
  item: GossipItem;
  variant?: "default" | "featured" | "compact";
}) {
  if (variant === "compact") {
    return (
      <article className="group">
        <Link
          to={`/gossip/${item.id}`}
          className="flex items-center gap-4 py-3 border-b border-background-200 hover:border-primary-300 transition-colors"
        >
          <span className="shrink-0 w-16 h-16 rounded-control overflow-hidden bg-background-100">
            <img
              src={item.cover}
              alt={item.coverAlt}
              className="w-full h-full object-cover object-top"
            />
          </span>
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 mb-1">
              <span className="text-[11px] text-secondary-700">{item.category}</span>
              <VerdictBadge verdict={item.verdict} size="sm" showIcon={false} />
            </span>
            <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
              {item.title}
            </span>
            <span className="mt-1.5 flex items-center gap-3 text-[11px] text-foreground-400">
              <span>{item.publishedAt}</span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line"></i>
                {formatHeat(item.heat)}
              </span>
            </span>
          </span>
          <span className="hidden sm:block shrink-0 w-24">
            <CredibilityMeter score={item.credibility} verdict={item.verdict} size="sm" />
          </span>
        </Link>
      </article>
    );
  }

  const isFeatured = variant === "featured";

  return (
    <article className="group h-full flex flex-col border border-background-200 rounded-card bg-background-50 overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={`/gossip/${item.id}`} className="flex flex-col h-full">
        <div
          className={`relative overflow-hidden bg-background-100 ${
            isFeatured ? "aspect-[16/10]" : "aspect-[4/3]"
          }`}
        >
          <img
            src={item.cover}
            alt={item.coverAlt}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <span className="absolute top-3 left-3">
            <VerdictBadge verdict={item.verdict} />
          </span>
          {isFeatured && (
            <span className="absolute top-3 right-3 px-2 py-1 rounded-control bg-foreground-950/70 text-background-50 text-[11px]">
              <i className="ri-star-line mr-1"></i>本期重点鉴定
            </span>
          )}
        </div>

        <div className={`flex flex-col flex-1 ${isFeatured ? "p-5 md:p-6" : "p-4 md:p-5"}`}>
          <div className="flex items-center gap-3 text-[11px] text-foreground-400">
            <span className="px-2 py-0.5 rounded-control bg-secondary-100 text-secondary-800">
              {item.category}
            </span>
            <span>{item.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {formatHeat(item.heat)}
            </span>
          </div>

          <h3
            className={`mt-3 font-heading font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug ${
              isFeatured ? "text-lg md:text-xl" : "text-base md:text-[17px]"
            }`}
          >
            {item.title}
          </h3>

          <p
            className={`mt-2 text-sm text-foreground-600 leading-relaxed ${
              isFeatured ? "line-clamp-3" : "line-clamp-2"
            }`}
          >
            {item.summary}
          </p>

          <div className="mt-auto pt-4">
            <CredibilityMeter score={item.credibility} verdict={item.verdict} size={isFeatured ? "md" : "sm"} />
            <div className="mt-3 flex items-center justify-between text-[11px] text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-links-line"></i>
                {item.evidence.length} 条证据 · {item.readMinutes} 分钟阅读
              </span>
              <span className="text-primary-600 font-medium flex items-center gap-0.5">
                查看证据链
                <i className="ri-arrow-right-s-line"></i>
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}